import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Target, TrendingUp, TrendingDown, RefreshCw, Zap, DollarSign,
  ArrowUpRight, ArrowDownRight, Activity, BarChart3, Clock, Star,
  AlertTriangle, Shield, Flame, Plus, X, List, Search
} from "lucide-react";

export default function SohelPick() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [newSymbol, setNewSymbol] = useState("");
  const [addingSymbol, setAddingSymbol] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get("/sohel-pick");
      setData(res.data);
      if (res.data.is_loading) {
        // Poll again in 5 seconds if still loading
        setTimeout(fetchData, 5000);
      }
    } catch (err) {
      console.error("Error fetching Sohel Pick:", err);
      toast.error("Failed to load picks");
    } finally {
      setLoading(false);
    }
  }, []);
  
  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await api.get("/sohel-pick/watchlist");
      setWatchlist(res.data.watchlist || []);
    } catch (err) {
      console.error("Error fetching watchlist:", err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchWatchlist();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchData, 300000);
    return () => clearInterval(interval);
  }, [fetchData, fetchWatchlist]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/sohel-pick/refresh");
      toast.success("Analyzing your portfolio... This takes ~60 seconds");
      // Poll for results
      setTimeout(fetchData, 5000);
      setTimeout(fetchData, 15000);
      setTimeout(fetchData, 30000);
      setTimeout(fetchData, 60000);
    } catch (err) {
      toast.error("Refresh failed");
    } finally {
      setTimeout(() => setRefreshing(false), 60000);
    }
  };
  
  const handleAddStock = async () => {
    if (!newSymbol.trim()) return;
    
    setAddingSymbol(true);
    try {
      const res = await api.post("/sohel-pick/watchlist/add", { symbol: newSymbol.trim() });
      toast.success(res.data.message);
      setNewSymbol("");
      fetchWatchlist();
      // Trigger refresh to include new stock
      handleRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add stock");
    } finally {
      setAddingSymbol(false);
    }
  };
  
  const handleRemoveStock = async (symbol) => {
    try {
      const res = await api.delete(`/sohel-pick/watchlist/${symbol}`);
      toast.success(res.data.message);
      fetchWatchlist();
    } catch (err) {
      toast.error("Failed to remove stock");
    }
  };
  
  // Search stocks by name or symbol
  const handleSearch = async (query) => {
    setNewSymbol(query);
    
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get(`/sohel-pick/search?q=${encodeURIComponent(query)}`);
        setSearchResults(res.data.results || []);
        setShowResults(true);
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setSearching(false);
      }
    }, 300); // Debounce 300ms
  };
  
  const handleSelectStock = (stock) => {
    setNewSymbol(stock.symbol);
    setShowResults(false);
    setSearchResults([]);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 p-4" data-testid="sohel-pick-loading">
        <div className="h-20 skeleton rounded-lg" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  const isLoading = data?.is_loading;
  const buyCallsCount = data?.buy_calls?.length || 0;
  const buyPutsCount = data?.buy_puts?.length || 0;
  const unusualCount = data?.unusual_activity?.length || 0;

  return (
    <div className="space-y-6 p-4" data-testid="sohel-pick-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl sm:text-3xl flex items-center gap-2">
            <Target className="w-7 h-7 text-yellow-400" />
            SOHEL PICK
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your portfolio stocks analyzed with Whale Flow + Technicals
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing || isLoading}
          className="bg-yellow-500 hover:bg-yellow-600 text-black gap-2"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${(refreshing || isLoading) ? "animate-spin" : ""}`} />
          {refreshing || isLoading ? "Scanning..." : "Scan Now"}
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="card-dark border-emerald-500/30">
          <CardContent className="p-3 text-center">
            <TrendingUp className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <p className="text-2xl font-bold font-data text-emerald-400">{buyCallsCount}</p>
            <p className="text-xs text-muted-foreground">BUY CALLS</p>
          </CardContent>
        </Card>
        <Card className="card-dark border-red-500/30">
          <CardContent className="p-3 text-center">
            <TrendingDown className="w-5 h-5 text-red-400 mx-auto mb-1" />
            <p className="text-2xl font-bold font-data text-red-400">{buyPutsCount}</p>
            <p className="text-xs text-muted-foreground">BUY PUTS</p>
          </CardContent>
        </Card>
        <Card className="card-dark border-yellow-500/30">
          <CardContent className="p-3 text-center">
            <Flame className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
            <p className="text-2xl font-bold font-data text-yellow-400">{unusualCount}</p>
            <p className="text-xs text-muted-foreground">HOT PICKS</p>
          </CardContent>
        </Card>
        <Card className="card-dark">
          <CardContent className="p-3 text-center">
            <BarChart3 className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-2xl font-bold font-data">{data?.total_portfolio_stocks || 0}</p>
            <p className="text-xs text-muted-foreground">TRACKED</p>
          </CardContent>
        </Card>
      </div>

      {/* Scan Time */}
      {data?.scan_time && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          Last scan: {new Date(data.scan_time).toLocaleString()}
        </div>
      )}

      {/* Add Stock to Watchlist */}
      <Card className="card-dark border-blue-500/30">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Plus className="w-4 h-4 text-blue-400" />
            <span className="font-heading font-bold text-sm">Add Stock to Watchlist</span>
          </div>
          <div className="flex gap-2 relative" ref={searchRef}>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or symbol (e.g., NVIDIA, AAPL)"
                value={newSymbol}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowResults(true)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddStock()}
                className="pl-9"
                data-testid="add-stock-input"
              />
              {searching && (
                <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
              )}
              
              {/* Search Results Dropdown */}
              {showResults && searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50 overflow-hidden">
                  {searchResults.map((stock) => (
                    <button
                      key={stock.symbol}
                      onClick={() => handleSelectStock(stock)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-500/20 flex items-center justify-between border-b border-gray-800 last:border-0 transition-colors"
                    >
                      <div>
                        <span className="font-bold text-blue-400">{stock.symbol}</span>
                        <span className="text-muted-foreground ml-2 text-sm">{stock.name}</span>
                      </div>
                      <Plus className="w-4 h-4 text-muted-foreground" />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button
              onClick={handleAddStock}
              disabled={addingSymbol || !newSymbol.trim()}
              className="bg-blue-500 hover:bg-blue-600 gap-1"
              data-testid="add-stock-btn"
            >
              {addingSymbol ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </Button>
          </div>
          
          {/* Current Watchlist */}
          {watchlist.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <List className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Custom Watchlist ({watchlist.length})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {watchlist.map((item) => (
                  <Badge
                    key={item.symbol}
                    variant="outline"
                    className="gap-1 pr-1 hover:bg-red-500/10 cursor-pointer group"
                  >
                    {item.symbol}
                    <button
                      onClick={() => handleRemoveStock(item.symbol)}
                      className="ml-1 hover:text-red-400 opacity-50 group-hover:opacity-100"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {isLoading ? (
        <Card className="card-dark">
          <CardContent className="p-8 text-center">
            <RefreshCw className="w-12 h-12 text-yellow-400 mx-auto mb-4 animate-spin" />
            <p className="text-lg font-heading">Analyzing Your Portfolio...</p>
            <p className="text-sm text-muted-foreground mt-2">{data?.message}</p>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="calls" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="calls" className="gap-1" data-testid="calls-tab">
              <TrendingUp className="w-3 h-3" /> Calls ({buyCallsCount})
            </TabsTrigger>
            <TabsTrigger value="puts" className="gap-1" data-testid="puts-tab">
              <TrendingDown className="w-3 h-3" /> Puts ({buyPutsCount})
            </TabsTrigger>
            <TabsTrigger value="unusual" className="gap-1" data-testid="unusual-tab">
              <Flame className="w-3 h-3" /> Hot ({unusualCount})
            </TabsTrigger>
            <TabsTrigger value="hold" className="gap-1" data-testid="hold-tab">
              <Shield className="w-3 h-3" /> Hold
            </TabsTrigger>
          </TabsList>

          {/* BUY CALLS TAB */}
          <TabsContent value="calls" className="mt-4 space-y-3">
            {data?.buy_calls?.length === 0 ? (
              <Card className="card-dark">
                <CardContent className="p-6 text-center text-muted-foreground">
                  No strong BUY CALL signals at the moment
                </CardContent>
              </Card>
            ) : (
              data?.buy_calls?.map((pick, idx) => (
                <PickCard key={pick.symbol + idx} pick={pick} type="call" />
              ))
            )}
          </TabsContent>

          {/* BUY PUTS TAB */}
          <TabsContent value="puts" className="mt-4 space-y-3">
            {data?.buy_puts?.length === 0 ? (
              <Card className="card-dark">
                <CardContent className="p-6 text-center text-muted-foreground">
                  No strong BUY PUT signals at the moment
                </CardContent>
              </Card>
            ) : (
              data?.buy_puts?.map((pick, idx) => (
                <PickCard key={pick.symbol + idx} pick={pick} type="put" />
              ))
            )}
          </TabsContent>

          {/* UNUSUAL/HOT TAB */}
          <TabsContent value="unusual" className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground mb-2">
              Stocks NOT in your portfolio with unusual whale activity
            </p>
            {data?.unusual_activity?.length === 0 ? (
              <Card className="card-dark">
                <CardContent className="p-6 text-center text-muted-foreground">
                  No unusual activity detected in watchlist stocks
                </CardContent>
              </Card>
            ) : (
              data?.unusual_activity?.map((pick, idx) => (
                <PickCard key={pick.symbol + idx} pick={pick} type={pick.action === "BUY CALLS" ? "call" : "put"} isHot />
              ))
            )}
          </TabsContent>

          {/* HOLD TAB */}
          <TabsContent value="hold" className="mt-4 space-y-3">
            <p className="text-xs text-muted-foreground mb-2">
              Portfolio stocks with no strong signal - hold current position
            </p>
            {data?.hold?.slice(0, 15).map((pick, idx) => (
              <Card key={pick.symbol + idx} className="card-dark opacity-70">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-bold">{pick.symbol}</span>
                      <span className="text-sm text-muted-foreground">${pick.current_price}</span>
                    </div>
                    <Badge variant="outline">HOLD</Badge>
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                    <span>RSI: {pick.rsi}</span>
                    <span>Whale: {pick.whale_sentiment}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function PickCard({ pick, type, isHot = false }) {
  const isCall = type === "call";
  
  return (
    <Card className={`card-dark ${isCall ? 'border-emerald-500/30' : 'border-red-500/30'} ${isHot ? 'border-yellow-500/50' : ''}`} data-testid={`pick-card-${pick.symbol}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {isHot && <Flame className="w-4 h-4 text-yellow-400" />}
            <span className="font-heading font-bold text-lg">{pick.symbol}</span>
            {pick.is_portfolio && (
              <Badge variant="outline" className="text-xs">YOUR STOCK</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge className={`${isCall ? 'bg-emerald-500' : 'bg-red-500'} text-white font-bold`}>
              {pick.action}
            </Badge>
            <span className={`font-bold ${pick.confidence >= 70 ? 'text-yellow-400' : pick.confidence >= 50 ? 'text-blue-400' : 'text-gray-400'}`}>
              {pick.confidence}%
            </span>
          </div>
        </div>

        {/* Price Info */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-xs text-muted-foreground">Current</p>
            <p className="font-data font-bold">${pick.current_price}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-xs text-muted-foreground">Target</p>
            <p className={`font-data font-bold ${isCall ? 'text-emerald-400' : 'text-red-400'}`}>${pick.target_price}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-xs text-muted-foreground">Stop</p>
            <p className="font-data text-yellow-400">${pick.stop_loss}</p>
          </div>
          <div className="bg-black/30 rounded p-2 text-center">
            <p className="text-xs text-muted-foreground">Potential</p>
            <p className={`font-data font-bold ${isCall ? 'text-emerald-400' : 'text-red-400'}`}>{pick.potential_gain}</p>
          </div>
        </div>

        {/* Technical Indicators */}
        <div className="flex flex-wrap gap-2 mb-3">
          <Badge variant="outline" className="text-xs">
            RSI: {pick.rsi}
          </Badge>
          <Badge variant="outline" className="text-xs">
            Vol: {pick.volume_ratio}x
          </Badge>
          <Badge variant="outline" className={`text-xs ${pick.daily_change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            Day: {pick.daily_change >= 0 ? '+' : ''}{pick.daily_change}%
          </Badge>
          <Badge className={`text-xs ${pick.whale_sentiment === 'BULLISH' ? 'bg-emerald-500/20 text-emerald-400' : pick.whale_sentiment === 'BEARISH' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20'}`}>
            Whale: {pick.whale_sentiment}
          </Badge>
        </div>

        {/* Whale Trade */}
        {pick.top_whale_trade && (
          <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 mb-3">
            <p className="text-xs text-blue-400 flex items-center gap-1">
              <Zap className="w-3 h-3" /> TOP WHALE TRADE
            </p>
            <p className="text-sm font-data">{pick.top_whale_trade}</p>
          </div>
        )}

        {/* Reasons */}
        <div className="space-y-1">
          {pick.reasons?.map((reason, idx) => (
            <p key={idx} className="text-xs text-muted-foreground flex items-center gap-1">
              {reason.includes("WHALE") ? (
                <Activity className="w-3 h-3 text-blue-400" />
              ) : (
                <ArrowUpRight className="w-3 h-3 text-gray-400" />
              )}
              {reason}
            </p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
